import React from "react";
import { motion } from "framer-motion";
import { CambridgeSummerConfig } from "../../../data/cambridgeSummerConfig";
import { Program } from "../../../types/program";

interface HeroProps {
    config: CambridgeSummerConfig;
    program2Weeks?: Program | null;
    program4Weeks?: Program | null;
}

const Hero: React.FC<HeroProps> = ({ config, program2Weeks, program4Weeks }) => {
    const { hero } = config;

    // 4 Weeks Program (Left card)
    const isEarlyBird4Weeks = program4Weeks?.is_early_bird_active === true;
    const displayPrice4Weeks = program4Weeks
        ? (program4Weeks.formatted_current_price ?? program4Weeks.formatted_price)
        : "TBA";
    const normalPrice4Weeks = program4Weeks?.formatted_price ?? "$0.00";
    const earlyBirdEnd4Weeks = program4Weeks?.is_early_bird_active && program4Weeks?.early_bird_ends_at
        ? new Date(program4Weeks.early_bird_ends_at).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : null;

    // 2 Weeks Program (Right card)
    const isEarlyBird2Weeks = program2Weeks?.is_early_bird_active === true;
    const displayPrice2Weeks = program2Weeks
        ? (program2Weeks.formatted_current_price ?? program2Weeks.formatted_price)
        : "TBA";
    const normalPrice2Weeks = program2Weeks?.formatted_price ?? "$0.00";
    const earlyBirdEnd2Weeks = program2Weeks?.is_early_bird_active && program2Weeks?.early_bird_ends_at
        ? new Date(program2Weeks.early_bird_ends_at).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : null;

    return (
        <div className="relative">
            {/* Cambridge Blue gradient - Academic/Innovation theme */}
            <div
                className="relative bg-gradient-to-br from-[#003C71] via-[#0066CC] to-[#003C71] pt-16 md:pt-20 pb-52 bg-cover bg-center"
                style={{ backgroundImage: hero.backgroundImage ? `url(${hero.backgroundImage})` : undefined }}
            >
                {hero.backgroundImage && <div className="absolute inset-0 bg-[#003C71]/85" />}
                <div className="relative z-10 text-center max-w-7xl mx-auto px-4 space-y-12">
                    <div className="space-y-4">
                        <div>
                            <motion.span
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="inline-block px-4 py-2 bg-[#9FCEF5]/20 backdrop-blur-sm rounded-full text-sm font-semibold text-[#9FCEF5] uppercase tracking-wider border border-[#9FCEF5]/40"
                            >
                                {hero.badge}
                            </motion.span>
                        </div>
                        <div>
                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-3xl md:text-4xl lg:text-6xl font-black text-white leading-tight"
                            >
                                {hero.title}
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="text-base md:text-lg text-gray-200 max-w-3xl mx-auto font-medium mt-4"
                            >
                                {hero.subtitle}
                            </motion.p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                        >
                            <a
                                href="#training-section"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const trainingSection = document.getElementById("training-section");
                                    if (trainingSection) {
                                        trainingSection.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }
                                }}
                                className="bg-[#8B1538] text-white px-10 py-4 rounded-lg font-medium text-sm md:text-base hover:bg-[#A51C30] transition-all duration-300 shadow-xl flex items-center transform hover:scale-105"
                            >
                                ENROLL NOW
                            </a>
                            <a
                                href="#schedule"
                                className="border border-white text-white px-10 py-4 rounded-lg font-medium text-sm md:text-base hover:bg-white hover:text-[#003C71] transition-all duration-300 flex items-center"
                            >
                                VIEW SCHEDULE
                                <img
                                    src="/images/view-all.svg"
                                    alt="Arrow Right"
                                    width={24}
                                    height={24}
                                    className="ml-2"
                                />
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="flex flex-col sm:flex-row items-center justify-center text-[#D1D5DB] font-normal text-sm md:text-base gap-x-4 gap-y-2 flex-wrap"
                        >
                            <div className="flex items-center gap-x-1">
                                <span className="text-[#9FCEF5] font-medium">Cambridge University College</span>
                            </div>
                            <div className="flex items-start gap-x-1">
                                <span>Official Certificate & Recommendation Letter</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Floating Info Card */}
            <div className="relative pb-[550px] sm:pb-[600px] md:pb-80 bg-white">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0 }}
                    className="absolute left-0 right-0 mx-auto -top-32 sm:-top-36 md:-top-40 w-full max-w-6xl px-4 z-20"
                >
                    <div className="bg-white rounded-[20px] shadow-2xl mx-auto p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Card - 4 Weeks Program */}
                            <div className="flex flex-col gap-y-4 h-full">
                                {/* Mobile Title - Only visible on mobile */}
                                <div className="lg:hidden text-center py-2 border-b-2 border-[#003C71]">
                                    <h3 className="text-lg font-bold text-[#003C71]">{program4Weeks?.duration || "4 Weeks"} Program</h3>
                                </div>
                                <div className="bg-[#F0F7FF] rounded-xl p-4 flex flex-col items-center justify-center text-center gap-y-2 h-full">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-center">
                                            <img src="/images/calendar.svg" alt="Duration" />
                                        </div>
                                        <p className="text-[#003C71] text-xs font-semibold uppercase">
                                            Duration
                                        </p>
                                    </div>
                                    <div className="space-y-0">
                                        <p className="text-[#121212] text-xl md:text-3xl font-bold">
                                            {program4Weeks?.duration || "4 Weeks"}
                                        </p>
                                        <p className="text-[#0066CC] text-sm font-semibold mt-1">
                                            Cambridge, UK
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-[#F0F7FF] rounded-xl p-4 flex flex-col items-center justify-center text-center gap-y-2 h-full">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-center">
                                            <img src="/images/money.svg" alt="Program Fee" />
                                        </div>
                                        <p className="text-[#003C71] text-xs font-semibold uppercase">
                                            Program Fee
                                        </p>
                                    </div>
                                    <p className="text-[#121212] text-xl md:text-3xl font-bold mt-1">
                                        {displayPrice4Weeks}
                                    </p>
                                    {isEarlyBird4Weeks && (
                                        <>
                                            <p className="text-[#6B7280] text-sm font-medium">
                                                Normal price {normalPrice4Weeks}
                                            </p>
                                            {earlyBirdEnd4Weeks && (
                                                <p className="text-[#8B1538] text-xs font-semibold">
                                                    Early bird until {earlyBirdEnd4Weeks}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Mobile Divider - Only visible on mobile */}
                            <div className="lg:hidden my-4">
                                <div className="flex items-center justify-center gap-4">
                                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#0066CC] to-transparent"></div>
                                    <div className="text-[#0066CC] font-semibold text-sm">OR</div>
                                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#0066CC] to-transparent"></div>
                                </div>
                            </div>

                            {/* Right Card - 2 Weeks Program */}
                            <div className="flex flex-col gap-y-4 h-full">
                                {/* Mobile Title - Only visible on mobile */}
                                <div className="lg:hidden text-center py-2 border-b-2 border-[#8B1538]">
                                    <h3 className="text-lg font-bold text-[#8B1538]">{program2Weeks?.duration || "2 Weeks"} Program</h3>
                                </div>
                                <div className="bg-[#F0F7FF] rounded-xl p-4 flex flex-col items-center justify-center text-center gap-y-2 h-full">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-center">
                                            <img src="/images/calendar.svg" alt="Duration" />
                                        </div>
                                        <p className="text-[#003C71] text-xs font-semibold uppercase">
                                            Duration
                                        </p>
                                    </div>
                                    <div className="space-y-0">
                                        <p className="text-[#121212] text-xl md:text-3xl font-bold">
                                            {program2Weeks?.duration || "2 Weeks"}
                                        </p>
                                        <p className="text-[#0066CC] text-sm font-semibold mt-1">
                                            Cambridge, UK
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-[#F0F7FF] rounded-xl p-4 flex flex-col items-center justify-center text-center gap-y-2 h-full">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-center">
                                            <img src="/images/money.svg" alt="Program Fee" />
                                        </div>
                                        <p className="text-[#003C71] text-xs font-semibold uppercase">
                                            Program Fee
                                        </p>
                                    </div>
                                    <p className="text-[#121212] text-xl md:text-3xl font-bold mt-1">
                                        {displayPrice2Weeks}
                                    </p>
                                    {isEarlyBird2Weeks && (
                                        <>
                                            <p className="text-[#6B7280] text-sm font-medium">
                                                Normal price {normalPrice2Weeks}
                                            </p>
                                            {earlyBirdEnd2Weeks && (
                                                <p className="text-[#8B1538] text-xs font-semibold">
                                                    Early bird until {earlyBirdEnd2Weeks}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Hero;
